import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy, inject } from '@angular/core';
import { AcaUiModule, AcaFaIcon } from '@aca/ui';
import { 
  faXmark, 
  faChevronLeft, 
  faChevronRight,
  faCircleCheck
} from '@fortawesome/pro-regular-svg-icons';
import { TourService, TourState, TourStep } from '../services/tour.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'acadv-tour-overlay',
  standalone: true,
  imports: [AcaUiModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (tourState.isActive) {
      <div class="tour-overlay-container">
      <!-- SVG Backdrop with true hole punch -->
      <svg class="tour-backdrop-svg" (click)="onBackdropClick()">
        <defs>
          <mask id="tour-spotlight-mask">
            <!-- Everything white becomes visible, black becomes transparent -->
            <rect width="100%" height="100%" fill="white" />
            <!-- Cut out the spotlight area (black = transparent hole) with smooth transitions -->
            @if (currentStep?.target) {
              <rect class="tour-spotlight-cutout"
                    [attr.x]="spotlightPosition.left"
                    [attr.y]="spotlightPosition.top"
                    [attr.width]="spotlightPosition.width"
                    [attr.height]="spotlightPosition.height"
                    [attr.rx]="8"
                    fill="black" />
            }
          </mask>
        </defs>
        <!-- Dark overlay with mask applied -->
        <rect width="100%" height="100%" 
              fill="rgba(0, 0, 0, 0.70)" 
              mask="url(#tour-spotlight-mask)" />
      </svg>
      
      <!-- Spotlight border and highlight effects with smooth transitions -->
      @if (currentStep?.target) {
        <div class="tour-spotlight-border" 
             [style.top.px]="spotlightPosition.top"
             [style.left.px]="spotlightPosition.left"
             [style.width.px]="spotlightPosition.width"
             [style.height.px]="spotlightPosition.height">
        </div>
      }

      <!-- Tour Step Card - Only render when positioning is complete -->
      @if (!isPositioning) {
        <div [class]="getCardPositionClass()"
             [style.top.px]="cardPosition.top"
             [style.left.px]="cardPosition.left">
        
        <!-- Header -->
        <div class="tour-card-header">
          <div class="tour-progress">
            <span class="tour-step-counter">Step {{ tourState.currentStepIndex + 1 }} of {{ tourState.totalSteps }}</span>
            <div class="tour-progress-bar">
              <div class="tour-progress-fill" 
                   [style.width.%]="progressPercentage">
              </div>
            </div>
          </div>
          <button class="tour-close-btn" (click)="closeTour()" aria-label="Close tour">
            <aca-icon [icon]="icons.close" class="close-icon"></aca-icon>
          </button>
        </div>

        <!-- Content -->
        <div class="tour-card-body">
          <h3 class="tour-card-title">{{ currentStep?.title }}</h3>
          <p class="tour-card-content">{{ currentStep?.content }}</p>
        </div>

        <!-- Footer with Navigation -->
        <div class="tour-card-footer">
          <button 
            class="tour-btn tour-btn-secondary" 
            [disabled]="tourState.currentStepIndex === 0"
            (click)="previousStep()">
            <aca-icon [icon]="icons.previous" class="btn-icon"></aca-icon>
            Previous
          </button>
          
          @if (tourState.currentStepIndex < tourState.totalSteps - 1) {
            <button 
              class="tour-btn tour-btn-primary" 
              (click)="nextStep()">
              Next
              <aca-icon [icon]="icons.next" class="btn-icon"></aca-icon>
            </button>
          }

          @if (tourState.currentStepIndex === tourState.totalSteps - 1) {
            <button 
              class="tour-btn tour-btn-success" 
              (click)="completeTour()">
              <aca-icon [icon]="icons.check" class="btn-icon"></aca-icon>
              Complete Tour
            </button>
          }
        </div>
        </div>
      }
      </div>
    }
  `,
  styles: [`
    .tour-overlay-container {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10000;
      pointer-events: auto;
      overflow: hidden;
    }

    .tour-backdrop-svg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: auto;
      cursor: default;
      z-index: 10000;
    }

    /* Smooth CSS transitions for SVG spotlight cutout */
    .tour-spotlight-cutout {
      transition: x 0.5s cubic-bezier(0.4, 0.0, 0.2, 1),
                  y 0.5s cubic-bezier(0.4, 0.0, 0.2, 1),
                  width 0.5s cubic-bezier(0.4, 0.0, 0.2, 1),
                  height 0.5s cubic-bezier(0.4, 0.0, 0.2, 1);
    }

    .tour-spotlight-border {
      position: absolute;
      border-radius: 8px;
      /* Subtle blue border to indicate the highlighted area */
      border: 2px solid rgba(59, 130, 246, 0.6);
      /* Light white overlay to brighten the area */
      background-color: rgba(255, 255, 255, 0.35);
      /* Smooth transitions matching the mask animation */
      transition: 
        top 0.5s cubic-bezier(0.4, 0.0, 0.2, 1),
        left 0.5s cubic-bezier(0.4, 0.0, 0.2, 1),
        width 0.5s cubic-bezier(0.4, 0.0, 0.2, 1),
        height 0.5s cubic-bezier(0.4, 0.0, 0.2, 1),
        opacity 0.3s ease;
      pointer-events: none;
      z-index: 10001;
    }

    /* Highlighted element style - balanced for readability */
    :global(.tour-highlight-element) {
      position: relative !important;
      z-index: 10002 !important;
      opacity: 1 !important;
      /* Good balance: bright enough to read, natural enough to not look artificial */
      filter: brightness(1.6) contrast(1.15) saturate(1.05) !important;
      /* Visible but not distracting border */
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5) !important;
      /* Smooth transition when highlight is applied/removed */
      transition: filter 0.4s cubic-bezier(0.4, 0.0, 0.2, 1),
                  box-shadow 0.4s cubic-bezier(0.4, 0.0, 0.2, 1) !important;
    }

    /* Prevent body scroll when tour is active */
    :global(body.tour-active) {
      overflow: hidden !important;
    }

    .tour-card {
      position: fixed;
      background-color: white;
      border-radius: 10px;
      box-shadow: 
        0 4px 6px rgba(0, 0, 0, 0.1),
        0 20px 40px rgba(0, 0, 0, 0.2);
      max-width: 280px;
      min-width: 260px;
      max-height: 80vh;
      overflow-y: auto;
      z-index: 10003;
      /* Polished entrance animation with scale and fade */
      animation: tourCardEnter 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    @keyframes tourCardEnter {
      0% {
        opacity: 0;
        transform: scale(0.85) translateY(20px);
      }
      60% {
        opacity: 0.8;
      }
      100% {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }

    .tour-card-center {
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) !important;
    }

    .tour-card-top {
      top: 20px;
    }

    .tour-card-bottom {
      bottom: 20px;
    }

    .tour-card-left {
      left: 20px;
    }

    .tour-card-right {
      right: 20px;
    }

    .tour-card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 0.875rem 0.875rem 0.75rem 0.875rem;
      border-bottom: 1px solid #e5e7eb;
    }

    .tour-progress {
      flex: 1;
    }

    .tour-step-counter {
      font-size: 0.75rem;
      font-weight: 500;
      color: #6b7280;
      display: block;
      margin-bottom: 0.375rem;
    }

    .tour-progress-bar {
      width: 100%;
      height: 4px;
      background-color: #e5e7eb;
      border-radius: 9999px;
      overflow: hidden;
    }

    .tour-progress-fill {
      height: 100%;
      background-color: #002855;
      transition: width 0.3s ease;
    }

    .tour-close-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.25rem;
      color: #6b7280;
      transition: color 0.2s;
      margin-left: 1rem;
    }

    .tour-close-btn:hover {
      color: #111827;
    }

    .close-icon {
      font-size: 1.25rem;
    }

    .tour-card-body {
      padding: 0.875rem;
    }

    .tour-card-title {
      font-size: 0.938rem;
      font-weight: 600;
      color: #111827;
      margin: 0 0 0.5rem 0;
      line-height: 1.3;
    }

    .tour-card-content {
      font-size: 0.781rem;
      color: #4b5563;
      line-height: 1.45;
      margin: 0;
    }

    .tour-card-footer {
      display: flex;
      justify-content: space-between;
      gap: 0.5rem;
      padding: 0.75rem 0.875rem 0.875rem 0.875rem;
      border-top: 1px solid #e5e7eb;
    }

    .tour-btn {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      padding: 0.5rem 0.75rem;
      border-radius: 0.375rem;
      font-size: 0.75rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
      white-space: nowrap;
    }

    .tour-btn-primary {
      background-color: #002855;
      color: white;
    }

    .tour-btn-primary:hover:not(:disabled) {
      background-color: #001a3d;
    }

    .tour-btn-success {
      background-color: #16a34a;
      color: white;
    }

    .tour-btn-success:hover:not(:disabled) {
      background-color: #15803d;
    }

    .tour-btn-secondary {
      background-color: white;
      color: #374151;
      border: 1px solid #d1d5db;
    }

    .tour-btn-secondary:hover:not(:disabled) {
      background-color: #f9fafb;
    }

    .tour-btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .btn-icon {
      font-size: 0.688rem;
    }
  `]
})
export class TourOverlayComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private currentHighlightedElement: Element | null = null;
  private cardElement: HTMLElement | null = null;

  tourState: TourState = {
    isActive: false,
    currentStepIndex: 0,
    totalSteps: 0
  };

  currentStep?: TourStep;
  spotlightPosition = { top: 0, left: 0, width: 0, height: 0 };
  cardPosition = { top: 0, left: 0 };
  isPositioning = false; // Hide card while calculating position

  icons = {
    close: new AcaFaIcon(faXmark),
    previous: new AcaFaIcon(faChevronLeft),
    next: new AcaFaIcon(faChevronRight),
    check: new AcaFaIcon(faCircleCheck)
  };

  get progressPercentage(): number {
    if (this.tourState.totalSteps === 0) return 0;
    return ((this.tourState.currentStepIndex + 1) / this.tourState.totalSteps) * 100;
  }

  private readonly tourService = inject(TourService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.tourService.tourState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.tourState = state;
        this.currentStep = state.currentStep;
        
        if (state.isActive && state.currentStep) {
          // Hide card immediately when step changes to prevent flash
          this.isPositioning = true;
          
          // Prevent body scrolling
          document.body.classList.add('tour-active');
          
          // Update positions after a brief delay for DOM to settle
          setTimeout(() => {
            this.updatePositions();
          }, 50);
        } else if (!state.isActive) {
          // Clean up when tour ends
          document.body.classList.remove('tour-active');
          this.cleanup();
        }
        this.cdr.markForCheck();
      });

    // Reposition card on window resize
    window.addEventListener('resize', this.handleResize);
    
    // Prevent scroll events on backdrop
    window.addEventListener('wheel', this.preventScroll, { passive: false });
    window.addEventListener('touchmove', this.preventScroll, { passive: false });
  }

  private handleResize = (): void => {
    if (this.tourState.isActive && this.currentStep) {
      this.updatePositions();
    }
  };

  private preventScroll = (event: Event): void => {
    if (this.tourState.isActive) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  ngOnDestroy(): void {
    this.cleanup();
    document.body.classList.remove('tour-active');
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('wheel', this.preventScroll);
    window.removeEventListener('touchmove', this.preventScroll);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private cleanup(): void {
    // Clean up highlighted element
    if (this.currentHighlightedElement) {
      this.currentHighlightedElement.classList.remove('tour-highlight-element');
      this.currentHighlightedElement = null;
    }
  }

  updatePositions(): void {
    // Note: isPositioning is already set to true in the subscribe block
    // Keep modal hidden during entire positioning process
    
    // Remove highlight class from previous element
    if (this.currentHighlightedElement) {
      this.currentHighlightedElement.classList.remove('tour-highlight-element');
    }

    if (!this.currentStep?.target || this.currentStep?.position === 'center') {
      // Center the card if no target or explicitly centered
      this.cardPosition = { top: 0, left: 0 };
      this.currentHighlightedElement = null;
      // Clear spotlight
      this.spotlightPosition = { top: 0, left: 0, width: 0, height: 0 };
      // Show card after a brief delay
      setTimeout(() => {
        this.isPositioning = false;
        this.cdr.markForCheck();
      }, 100);
      return;
    }

    const targetElement = document.querySelector(this.currentStep.target);
    if (!targetElement) {
      // Center the card if target not found
      this.cardPosition = { top: 0, left: 0 };
      this.currentHighlightedElement = null;
      this.spotlightPosition = { top: 0, left: 0, width: 0, height: 0 };
      setTimeout(() => {
        this.isPositioning = false;
        this.cdr.markForCheck();
      }, 100);
      return;
    }

    // Add highlight class to current element FIRST
    targetElement.classList.add('tour-highlight-element');
    this.currentHighlightedElement = targetElement;

    // Scroll element into view if needed
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

    // Wait for scroll to complete before positioning
    setTimeout(() => {
      const rect = targetElement.getBoundingClientRect();
      const padding = 12; // Increased padding for better separation

      // Set spotlight position to create a clear window around the element
      this.spotlightPosition = {
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + (padding * 2),
        height: rect.height + (padding * 2)
      };

      // Calculate card position based on position preference
      this.calculateCardPosition(rect);

      // Wait for spotlight animation (0.5s) + small buffer before showing modal
      // This creates a nice sequential reveal: spotlight -> then modal
      setTimeout(() => {
        this.isPositioning = false;
        this.cdr.markForCheck();
      }, 600);
    }, 350);
  }

  calculateCardPosition(targetRect: DOMRect): void {
    // Get actual card dimensions dynamically
    const cardElement = document.querySelector('.tour-card') as HTMLElement;
    let cardWidth = 280;
    let cardHeight = 240;
    
    if (cardElement) {
      const cardRect = cardElement.getBoundingClientRect();
      cardWidth = Math.max(cardRect.width || 280, 280);
      cardHeight = Math.max(cardRect.height || 240, 240);
    }

    const minSpacing = 50; // Much larger spacing to prevent any overlap
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 20; // Margin from viewport edges
    const spotlightPadding = 20; // Extra padding to ensure clearance around spotlight

    // Helper to check if position is valid (fully visible and doesn't overlap target)
    const isValidPosition = (top: number, left: number, allowPartialOverlap = false): boolean => {
      const right = left + cardWidth;
      const bottom = top + cardHeight;
      
      // Check viewport bounds - must be fully visible
      if (left < margin || right > viewportWidth - margin || 
          top < margin || bottom > viewportHeight - margin) {
        return false;
      }
      
      // Check if it overlaps with target (including spotlight padding)
      const targetWithPadding = {
        left: targetRect.left - spotlightPadding,
        right: targetRect.right + spotlightPadding,
        top: targetRect.top - spotlightPadding,
        bottom: targetRect.bottom + spotlightPadding
      };
      
      // Very conservative buffer for maximum clearance - prevents any overlap
      const buffer = allowPartialOverlap ? -5 : 20;
      const hasOverlap = !(
        right < targetWithPadding.left - buffer || 
        left > targetWithPadding.right + buffer || 
        bottom < targetWithPadding.top - buffer || 
        top > targetWithPadding.bottom + buffer
      );
      
      return !hasOverlap;
    };

    // Calculate all possible positions
    const positions = [
      // Primary positions (4 sides)
      {
        name: 'bottom',
        top: targetRect.bottom + spotlightPadding + minSpacing,
        left: Math.max(margin, Math.min(
          targetRect.left + (targetRect.width / 2) - (cardWidth / 2),
          viewportWidth - cardWidth - margin
        ))
      },
      {
        name: 'right',
        top: Math.max(margin, Math.min(
          targetRect.top + (targetRect.height / 2) - (cardHeight / 2),
          viewportHeight - cardHeight - margin
        )),
        left: targetRect.right + spotlightPadding + minSpacing
      },
      {
        name: 'top',
        top: targetRect.top - spotlightPadding - minSpacing - cardHeight,
        left: Math.max(margin, Math.min(
          targetRect.left + (targetRect.width / 2) - (cardWidth / 2),
          viewportWidth - cardWidth - margin
        ))
      },
      {
        name: 'left',
        top: Math.max(margin, Math.min(
          targetRect.top + (targetRect.height / 2) - (cardHeight / 2),
          viewportHeight - cardHeight - margin
        )),
        left: targetRect.left - spotlightPadding - minSpacing - cardWidth
      },
      // Secondary positions (corners)
      {
        name: 'bottom-right',
        top: targetRect.bottom + spotlightPadding + minSpacing,
        left: Math.min(
          targetRect.right + minSpacing,
          viewportWidth - cardWidth - margin
        )
      },
      {
        name: 'bottom-left',
        top: targetRect.bottom + spotlightPadding + minSpacing,
        left: Math.max(
          targetRect.left - cardWidth - minSpacing,
          margin
        )
      },
      {
        name: 'top-right',
        top: targetRect.top - spotlightPadding - minSpacing - cardHeight,
        left: Math.min(
          targetRect.right + minSpacing,
          viewportWidth - cardWidth - margin
        )
      },
      {
        name: 'top-left',
        top: targetRect.top - spotlightPadding - minSpacing - cardHeight,
        left: Math.max(
          targetRect.left - cardWidth - minSpacing,
          margin
        )
      }
    ];

    // Try preferred position first if specified
    const preferredPosition = this.currentStep?.position;
    if (preferredPosition && preferredPosition !== 'center') {
      const preferred = positions.find(p => p.name === preferredPosition);
      if (preferred && isValidPosition(preferred.top, preferred.left)) {
        this.cardPosition = { top: preferred.top, left: preferred.left };
        return;
      }
    }

    // Try all positions (strict - no overlap)
    for (const pos of positions) {
      if (isValidPosition(pos.top, pos.left, false)) {
        this.cardPosition = { top: pos.top, left: pos.left };
        return;
      }
    }

    // Second pass: allow slight overlap if no perfect position found
    for (const pos of positions) {
      if (isValidPosition(pos.top, pos.left, true)) {
        this.cardPosition = { top: pos.top, left: pos.left };
        return;
      }
    }

    // Fallback: Find the position with maximum available space
    const spaces = [
      { name: 'right', space: viewportWidth - targetRect.right },
      { name: 'left', space: targetRect.left },
      { name: 'bottom', space: viewportHeight - targetRect.bottom },
      { name: 'top', space: targetRect.top }
    ].sort((a, b) => b.space - a.space);

    const largestSpace = spaces[0];
    let top = margin;
    let left = margin;

    switch (largestSpace.name) {
      case 'right':
        left = Math.min(targetRect.right + spotlightPadding + minSpacing, viewportWidth - cardWidth - margin);
        top = Math.max(margin, Math.min(
          targetRect.top + (targetRect.height / 2) - (cardHeight / 2),
          viewportHeight - cardHeight - margin
        ));
        break;
      case 'left':
        left = Math.max(margin, targetRect.left - spotlightPadding - minSpacing - cardWidth);
        top = Math.max(margin, Math.min(
          targetRect.top + (targetRect.height / 2) - (cardHeight / 2),
          viewportHeight - cardHeight - margin
        ));
        break;
      case 'bottom':
        top = Math.min(targetRect.bottom + spotlightPadding + minSpacing, viewportHeight - cardHeight - margin);
        left = Math.max(margin, Math.min(
          targetRect.left + (targetRect.width / 2) - (cardWidth / 2),
          viewportWidth - cardWidth - margin
        ));
        break;
      case 'top':
        top = Math.max(margin, targetRect.top - spotlightPadding - minSpacing - cardHeight);
        left = Math.max(margin, Math.min(
          targetRect.left + (targetRect.width / 2) - (cardWidth / 2),
          viewportWidth - cardWidth - margin
        ));
        break;
    }

    // Final bounds check
    top = Math.max(margin, Math.min(top, viewportHeight - cardHeight - margin));
    left = Math.max(margin, Math.min(left, viewportWidth - cardWidth - margin));

    this.cardPosition = { top, left };
  }

  /**
   * Returns the full class string for the tour card element.
   * `tour-card` is always included here rather than as a static class attribute
   * so that [class] never overwrites the base styling.
   */
  getCardPositionClass(): string {
    if (!this.currentStep?.target) {
      return 'tour-card tour-card-center';
    }
    return 'tour-card';
  }

  nextStep(): void {
    this.tourService.nextStep();
  }

  previousStep(): void {
    this.tourService.previousStep();
  }

  completeTour(): void {
    this.tourService.endTour();
  }

  closeTour(): void {
    this.tourService.endTour();
  }

  onBackdropClick(): void {
    // Optionally close tour on backdrop click
    // this.closeTour();
  }
}

